import { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import {
  Column,
  ErrorPlaceholder,
  LoadingIndicator,
  RichTextEditorInput,
  Row,
  SheetSectionHeader,
  Text,
} from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useInputDraft } from "@/hooks/useInputDraft";
import { useCreateAddCandidacyEvaluationNew } from "@/hooks/createAdds/useCreateAddCandidacyEvaluationNew";
import { useGetCandidacyEvaluationNew } from "@/hooks/queries/useGetCandidacyEvaluationNew";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { candidacyEvaluations } from "@/lib/candidacyEvaluations";
import { CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";
import { CandidacyEvaluationFollowUp } from "@/types";

const AUTOSAVE_DEBOUNCE_MS = 250;

export const EvaluationEditor = ({ candidacyId }: { candidacyId: string }) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const evaluationQuery = useGetCandidacyEvaluationNew({
    candidacyId,
  });
  const candidacyInfo = useGetCandidacyInfo({
    candidacyId,
  }).data;
  // Create
  const { mutate: createEvaluation } = useCreateAddCandidacyEvaluationNew();

  /* React Hooks */
  // Edits that never reached the server (closed mid-debounce, failed save)
  // come back from the local draft; cleared once the autosave lands.
  const { clearDraft, draft, setDraft } = useInputDraft(
    `evaluation:${candidacyId}`,
  );
  const [content, setContent] = useState("");
  const [debouncedContent] = useDebounce(content, AUTOSAVE_DEBOUNCE_MS);
  const contentRef = useRef(content);
  const followUpsRef = useRef<CandidacyEvaluationFollowUp[]>([]);
  const hasInitializedRef = useRef(false);
  const isSavingRef = useRef(false);
  const lastSavedContentRef = useRef("");
  const [saveTick, setSaveTick] = useState(0);

  /* Helpers */
  // Followups come straight from the cache so additions made elsewhere
  // (e.g. the logistics "add to evaluation" control) show up immediately
  // and aren't clobbered by autosaves carrying a stale snapshot.
  const followUps = Array.isArray(evaluationQuery.data?.followUps)
    ? evaluationQuery.data.followUps
    : [];
  // Dedupe by title: followups added via the logistics control store a
  // different subtitle than the generated recommendation.
  const recommendedFollowUps = [
    candidacyEvaluations.getCommuteRecommendation({
      commuteDistanceMiles: candidacyInfo?.commuteDistanceMiles,
      commuteTimeMins: candidacyInfo?.commuteTimeMins,
    }),
  ]
    .filter((followUp): followUp is CandidacyEvaluationFollowUp => !!followUp)
    .filter(
      (recommendation) =>
        !followUps.some((followUp) => followUp.title === recommendation.title),
    );

  /* React Hooks */
  useEffect(() => {
    followUpsRef.current = followUps;
  });

  useEffect(() => {
    if (
      hasInitializedRef.current ||
      evaluationQuery.isPending ||
      evaluationQuery.isError
    ) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty HTML should remain empty
    const savedContent = evaluationQuery.data?.evaluation || "";
    // A lingering draft is an edit that never reached the server — resume from
    // it; the autosave then pushes it up and clears it.
    const initialContent = tiptap.isEmptyFn(draft) ? savedContent : draft;
    contentRef.current = initialContent;
    lastSavedContentRef.current = savedContent;
    setContent(initialContent);
    hasInitializedRef.current = true;
  }, [
    draft,
    evaluationQuery.data,
    evaluationQuery.isError,
    evaluationQuery.isPending,
  ]);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (debouncedContent === lastSavedContentRef.current) return;
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    mixpanelUtils.trackFormSubmission(
      "Evaluation Autosave",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    createEvaluation(
      {
        candidacyId,
        evaluation: {
          evaluation: debouncedContent,
          followUps: followUpsRef.current,
        },
      },
      {
        onSuccess: () => {
          lastSavedContentRef.current = debouncedContent;
          // Everything typed so far is on the server — the draft's job is done.
          if (contentRef.current === debouncedContent) clearDraft();
        },
        onSettled: () => {
          isSavingRef.current = false;
          setSaveTick((tick) => tick + 1);
        },
      },
    );
  }, [candidacyId, createEvaluation, debouncedContent, saveTick]);

  useEffect(() => {
    return () => {
      if (
        hasInitializedRef.current &&
        contentRef.current !== lastSavedContentRef.current
      ) {
        createEvaluation({
          candidacyId,
          evaluation: {
            evaluation: contentRef.current,
            followUps: followUpsRef.current,
          },
        });
      }
    };
  }, [candidacyId, createEvaluation]);

  /* Render */
  const renderFollowUps = () => {
    if (!followUps.length) return null;

    return (
      <Column className="gap-2">
        <SheetSectionHeader title="Followups" />
        <Column>
          {followUps.map(({ note, subtitle, title }) => (
            <Column
              className="gap-1 border-b border-slate-100 py-3 duration-300 animate-in slide-in-from-left-2 last:border-b-0"
              key={`${title}-${subtitle}`}
            >
              <Text className="text-sm font-medium text-slate-800">
                {title}
              </Text>
              <Text className="text-sm font-normal leading-snug text-slate-600">
                {subtitle}
              </Text>
              {note ? (
                <Row className="mt-1 items-stretch gap-2 py-1">
                  <Column className="w-1 shrink-0 self-stretch rounded-full bg-primary-300" />
                  <Column className="min-w-0 gap-0.5">
                    <Text
                      className={cn(
                        CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME,
                        "text-[10px] leading-3",
                      )}
                      variant="muted"
                    >
                      Note
                    </Text>
                    <Text className="text-sm font-normal leading-snug text-slate-600">
                      {note}
                    </Text>
                  </Column>
                </Row>
              ) : null}
            </Column>
          ))}
        </Column>
      </Column>
    );
  };

  const renderRecommendedFollowUps = () => {
    if (!recommendedFollowUps.length) return null;

    return (
      <Column className="gap-2">
        <SheetSectionHeader title="Recommended Followups" />
        <Column>
          {recommendedFollowUps.map(({ subtitle, title }) => (
            <Column className="gap-1 py-3" key={`${title}-${subtitle}`}>
              <Row className="items-start justify-between gap-3">
                <Text className="text-sm font-medium text-slate-800">
                  {title}
                </Text>
                <Text className="shrink-0 cursor-default rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                  Needs follow-up
                </Text>
              </Row>
              <Row className="mt-0.5 items-stretch gap-2 py-1">
                <Column className="w-1 shrink-0 self-stretch rounded-full bg-slate-300" />
                <Column className="min-w-0 gap-0.5">
                  <Text
                    className={cn(
                      CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME,
                      "text-[10px] leading-3",
                    )}
                    variant="muted"
                  >
                    Suggested ask
                  </Text>
                  <Text className="text-sm font-light leading-snug text-slate-600">
                    {subtitle}
                  </Text>
                </Column>
              </Row>
            </Column>
          ))}
        </Column>
      </Column>
    );
  };

  const render = () => {
    if (evaluationQuery.isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    // Never show an empty editor on error — typing into it would overwrite
    // the saved evaluation with blank content.
    if (evaluationQuery.isError) {
      return <ErrorPlaceholder />;
    }

    return (
      <Column className="shrink-0 gap-6 pb-[150px]">
        <Column className="gap-2">
          <RichTextEditorInput
            disabled={isReadOnly}
            editorContentClasses="h-64 px-2 py-1 text-sm"
            mode={{
              onChange: (value: string) => {
                setContent(value);
                setDraft(value);
              },
              type: "html",
              value: content,
            }}
          />
        </Column>
        {renderFollowUps()}
        {renderRecommendedFollowUps()}
      </Column>
    );
  };

  return render();
};
